import { Injectable } from '@angular/core';
import { BehaviorSubject, Observable, Subscription } from 'rxjs';
import { intervalBackoff } from 'backoff-rxjs';
import { AlertMessageStore } from '../../core/stores/alert-message.store';
import { LoggingService } from '../../core/services/logging.service';
import { SeverityTypeEnum } from '../../core/enums/severity-type.enum';
import { AlertMessageFactory } from '../../core/factories/alert-message.factory';
import { AlertMessageModel } from '../../core/models/alert-message.model';
import { AlertContextEnum } from '../../core/enums/alert-context.enum';
import { ClientQuestionnaireModel } from '../../../../../../../common/dist/angular/models/client-questionnaire.model';
import { ClientQuestionnairesApiController } from '../api-controllers/client-questionnaires.api-controller';
import {
  ClientQuestionnaireCreationOptions,
} from '../../../../../../../common/src/options/client-questionnaire/client-questionnaire-creation.options';
import {
  ClientQuestionnaireOptions,
} from '../../../../../../../common/src/options/client-questionnaire/client-questionnaire.options';

@Injectable({
  providedIn: 'root',
})
export class ClientQuestionnaireStore {
  private autoRefreshStore: { [id: string]: Subscription } = {};

  private store: { [id: string]: BehaviorSubject<ClientQuestionnaireModel | undefined> } = {};

  private errorsStore: { [id: string]: BehaviorSubject<string[]> } = {};

  public constructor(
    private readonly clientQuestionnairesApiController: ClientQuestionnairesApiController,
    private readonly loggingService: LoggingService,
    private readonly alertMessageStore: AlertMessageStore) {
  }

  public exists(id: string): boolean {
    return this.store.hasOwnProperty(id);
  }

  public get(id: string): Observable<ClientQuestionnaireModel | undefined> {
    if (this.store.hasOwnProperty(id) === false) {
      this.store[id] = new BehaviorSubject<ClientQuestionnaireModel | undefined>(undefined);

      this.refresh(id);
    }

    return this.store[id];
  }

  public getErrors(id: string): Observable<string[]> {
    if (this.errorsStore.hasOwnProperty(id) === false) {
      this.errorsStore[id] = new BehaviorSubject<string[]>([]);
    }

    return this.errorsStore[id];
  }

  public setErrors(id: string, validationErrors: string[] = []) {
    if (this.errorsStore.hasOwnProperty(id) === false) {
      this.errorsStore[id] = new BehaviorSubject<string[]>([]);
    }

    this.errorsStore[id].next(validationErrors);
  }

  public async save(id: string, model: ClientQuestionnaireModel): Promise<void> {
    if (this.store.hasOwnProperty(id) === false) {
      this.store[id] = new BehaviorSubject<ClientQuestionnaireModel | undefined>(model);
    } else {
      this.store[id].next(model);
    }
  }

  public async refresh(id: string): Promise<void> {
    this.loggingService.debug('Refreshing questionnaire', { id });

    try {
      const model = await this.clientQuestionnairesApiController.get(id);

      await this.save(id, model);
    } catch (error) {
      this.loggingService.error('[client-questionnaire-store] - There was an error retrieving theClient Questionnaire Form', { error });

      if (error.status === 404) {
        this.remove(id);
      }

      this.alertMessageStore.saveAll(AlertMessageFactory.create(error, SeverityTypeEnum.Error));
    }
  }

  public async update(id: string, clientQuestionnaireOptions: ClientQuestionnaireOptions): Promise<void> {
    try {
      const model = await this.clientQuestionnairesApiController.updateOptions(id, clientQuestionnaireOptions);

      await this.save(id, model);

      this.alertMessageStore.save(new AlertMessageModel(SeverityTypeEnum.Success, AlertContextEnum.Global, {
        fr: 'Le formulaire a bien été sauvegardé',
        en: 'The form has been successfully saved.',
      }));
    } catch (error) {
      this.loggingService.error('[client-questionnaire-store] - There was an error updating the client Questionnaire Form', { error });

      this.alertMessageStore.saveAll(AlertMessageFactory.create(error, SeverityTypeEnum.Error));

      throw error;
    }
  }

  public async finalize(id: string, clientQuestionnaireOptions: ClientQuestionnaireOptions): Promise<void> {
    try {
      const model = await this.clientQuestionnairesApiController.finalize(id, clientQuestionnaireOptions);

      await this.save(id, model);
    } catch (error) {
      this.loggingService.error('[client-questionnaire-store] - There was an error finalizing the client questionnaire', { error });

      this.alertMessageStore.saveAll(AlertMessageFactory.create(error, SeverityTypeEnum.Error));

      throw error;
    }
  }

  public async forceFinalize(id: string, clientQuestionnaireOptions: ClientQuestionnaireOptions): Promise<void> {
    try {
      const model = await this.clientQuestionnairesApiController.forceFinalize(id, clientQuestionnaireOptions);

      await this.save(id, model);
    } catch (error) {
      this.loggingService.error('[client-questionnaire-store] - There was an error force finalizing the client questionnaire', { error });

      this.alertMessageStore.saveAll(AlertMessageFactory.create(error, SeverityTypeEnum.Error));

      throw error;
    }
  }

  public async create(options: ClientQuestionnaireCreationOptions): Promise<Observable<ClientQuestionnaireModel>> {
    try {
      const model = await this.clientQuestionnairesApiController.create(options);

      await this.save(model.id, model);

      return this.store[model.id];
    } catch (error) {
      this.loggingService.error('[client-questionnaire-store] - There was an error creating theClient Questionnaire Form', { error });

      this.alertMessageStore.saveAll(AlertMessageFactory.create(error, SeverityTypeEnum.Error));

      throw error;
    }
  }

  // public async delete(id: string): Promise<void> {
  //     try {
  //         await this.clientQuestionnairesApiController.delete(id);
  //
  //         await this.indexedDbService.clientQuestionnaires.delete(id);
  //         this.remove(id);
  //         this.alertMessageStore.save(new AlertMessageModel(SeverityTypeEnum.Success, AlertContextEnum.CalculationDeleted, {
  //             fr: "Le formulaire a bien été suprrimé",
  //             en: "The calculation has been successfully deleted."
  //         }))
  //     } catch (error) {
  //         this.loggingService.error("[client-questionnaire-store] - There was an error deleting theClient Questionnaire Form", {error})
  //
  //         this.alertMessageStore.saveAll(AlertMessageFactory.create(error, SeverityTypeEnum.Error))
  //
  //         throw error;
  //     }
  // }

  public startAutoRefresh(id: string): void {
    if (this.autoRefreshStore[id]) {
      return;
    }
    this.autoRefreshStore[id] = intervalBackoff(10000).subscribe(async () => {
      await this.refresh(id);
    });
  }

  public stopAutoRefresh(id: string): void {
    if (!this.autoRefreshStore[id]) {
      return;
    }
    this.autoRefreshStore[id].unsubscribe();
    this.autoRefreshStore[id] = undefined;
  }

  public remove(id: string): void {
    this.store[id].complete();
    delete this.store[id];
  }
}
